import { Fragment, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronRight,
  faEllipsis,
  faHouse,
} from "@fortawesome/free-solid-svg-icons";
import BreadcrumbMenu from "./BreadcrumbMenu";
import { useBreadcrumbContributions } from "./BreadcrumbProvider";
import { buildTrail } from "./buildTrail";
import { CRUMB_KIND } from "./constants";
import { hiddenItems, hiddenSelectedKeys, sectionItems } from "./utils";

const ELLIPSIS = "ellipsis";
const DESKTOP_MAX_VISIBLE = 5;
const MOBILE_MAX_VISIBLE = 3;

const CRUMB_CLASS =
  "flex h-7 min-w-0 items-center gap-1.5 rounded-md px-2 text-secondary transition-colors hover:bg-primary-bg-soft hover:text-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-primary";

// Home, an ellipsis holding the middle, and the last levels up to `maxVisible`.
function collapseTrail(crumbs, maxVisible) {
  if (crumbs.length <= maxVisible) return crumbs;
  const keptAtEnd = maxVisible - 2;
  return [
    crumbs[0],
    {
      key: ELLIPSIS,
      kind: ELLIPSIS,
      hidden: crumbs.slice(1, crumbs.length - keptAtEnd),
    },
    ...crumbs.slice(crumbs.length - keptAtEnd),
  ];
}

function HomeIcon() {
  return <FontAwesomeIcon icon={faHouse} className="text-[13px]" />;
}

function Crumb({ crumb, isCurrent, asSheet }) {
  const { t } = useTranslation();
  const isHome = crumb.kind === CRUMB_KIND.HOME;
  // On a phone the house alone stands for Home.
  const label = (
    <span className={`truncate ${isHome ? "sr-only sm:not-sr-only" : ""}`}>
      {crumb.label}
    </span>
  );

  if (crumb.kind === ELLIPSIS) {
    return (
      <BreadcrumbMenu
        items={hiddenItems(crumb.hidden)}
        selectedKeys={hiddenSelectedKeys(crumb.hidden)}
        ariaLabel={t("breadcrumbs.hiddenLevels")}
        title={t("breadcrumbs.goTo")}
        asSheet={asSheet}
        className={CRUMB_CLASS}
      >
        <FontAwesomeIcon icon={faEllipsis} className="text-[14px]" />
      </BreadcrumbMenu>
    );
  }

  if (isCurrent) {
    return (
      <span
        aria-current="page"
        title={crumb.label}
        className="flex min-w-0 max-w-[280px] items-center gap-1.5 px-2 font-bold text-heading"
      >
        {isHome && <HomeIcon />}
        {label}
      </span>
    );
  }

  if (crumb.kind === CRUMB_KIND.SECTION) {
    const items = sectionItems(crumb.entry);
    if (!items.length) {
      return <span className="px-2 text-secondary">{crumb.label}</span>;
    }
    return (
      <BreadcrumbMenu
        items={items}
        selectedKeys={crumb.activePath ? [crumb.activePath] : []}
        title={crumb.label}
        asSheet={asSheet}
        className={CRUMB_CLASS}
      >
        <span className="max-w-[220px] truncate">{crumb.label}</span>
        <FontAwesomeIcon icon={faChevronDown} className="text-[9px]" />
      </BreadcrumbMenu>
    );
  }

  // No url: the user may not open that page, so it names the level without linking it.
  if (!crumb.url) {
    return (
      <span className="flex min-w-0 max-w-[220px] items-center gap-1.5 px-2 text-secondary">
        {isHome && <HomeIcon />}
        {label}
      </span>
    );
  }

  return (
    <Link to={crumb.url} title={crumb.label} className={CRUMB_CLASS}>
      {isHome && <HomeIcon />}
      <span
        className={`max-w-[220px] truncate ${isHome ? "sr-only sm:not-sr-only" : ""}`}
      >
        {crumb.label}
      </span>
    </Link>
  );
}

function BreadcrumbList({ crumbs, className, asSheet = false }) {
  return (
    <ol
      className={`m-0 list-none items-center gap-0.5 p-0 text-[13px] leading-5 ${className}`}
    >
      {crumbs.map((crumb, index) => (
        <Fragment key={crumb.key}>
          {index > 0 && (
            <li aria-hidden="true" className="flex text-text-subtle">
              <FontAwesomeIcon
                icon={faChevronRight}
                className="text-[10px] rtl:rotate-180"
              />
            </li>
          )}
          <li className="flex min-w-0 items-center">
            <Crumb
              crumb={crumb}
              isCurrent={index === crumbs.length - 1}
              asSheet={asSheet}
            />
          </li>
        </Fragment>
      ))}
    </ol>
  );
}

/**
 * The trail at the top of every page, from Home to the page you are on. Rendered
 * once by AppLayout; pages only feed it names through the useBreadcrumb* hooks.
 */
export default function Breadcrumbs() {
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const contributions = useBreadcrumbContributions();

  const crumbs = useMemo(
    () => buildTrail({ pathname, search, contributions, t }),
    [pathname, search, contributions, t]
  );

  if (!crumbs.length) return null;

  return (
    <nav aria-label={t("breadcrumbs.ariaLabel")} className="mb-[18px]">
      <BreadcrumbList
        crumbs={collapseTrail(crumbs, DESKTOP_MAX_VISIBLE)}
        className="hidden sm:flex sm:flex-wrap"
      />
      <BreadcrumbList
        crumbs={collapseTrail(crumbs, MOBILE_MAX_VISIBLE)}
        // One line on a phone: the crumbs shrink and truncate rather than wrap.
        className="flex sm:hidden"
        // Menus open as a bottom sheet here: there is no room for a submenu beside one.
        asSheet
      />
    </nav>
  );
}
